import { useEffect, useState, type CSSProperties } from 'react';
import type { Customer } from '@setapart/shared';
import { FONTS, COLORS } from '@setapart/shared';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { VerifyCallback } from './pages/VerifyCallback';
import { ConnectStripe } from './pages/ConnectStripe';
import { me, logout } from './lib/api';

type Route = 'login' | 'dashboard' | 'verify' | 'connect-stripe' | 'connect-stripe-return';

function currentRoute(): Route {
  const p = window.location.pathname;
  if (p.startsWith('/verify')) return 'verify';
  if (p.startsWith('/connect/stripe/return')) return 'connect-stripe-return';
  if (p.startsWith('/connect/stripe')) return 'connect-stripe';
  if (p.startsWith('/dashboard')) return 'dashboard';
  return 'login';
}

export default function App() {
  const [route, setRoute] = useState<Route>(currentRoute);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const onPop = () => setRoute(currentRoute());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    (async () => {
      const res = await me();
      setCustomer(res.customer);
      setLoading(false);
    })();
  }, []);

  function navigate(path: string) {
    window.history.pushState({}, '', path);
    setRoute(currentRoute());
  }

  async function onSignOut() {
    await logout();
    setCustomer(null);
    navigate('/');
  }

  return (
    <div style={shellStyle}>
      <header style={headerStyle}>
        <div style={{ fontFamily: FONTS.display, fontSize: 18, letterSpacing: '-0.02em', color: COLORS.ink }}>
          <span style={{ fontStyle: 'italic', fontWeight: 400 }}>Set</span>{' '}
          <span style={{ fontWeight: 300 }}>Apart</span>{' '}
          <span style={{ fontSize: 9, letterSpacing: '0.4em', textTransform: 'uppercase', color: COLORS.muted, fontWeight: 700, marginLeft: 8 }}>
            CREATOR PORTAL
          </span>
        </div>
        {customer && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: COLORS.muted }}>{customer.email}</span>
            <button onClick={onSignOut} style={smallBtn}>Sign out</button>
          </div>
        )}
      </header>

      <main style={mainStyle}>
        {loading && <div style={{ color: COLORS.muted, padding: 32 }}>Loading…</div>}

        {!loading && route === 'verify' && (
          <VerifyCallback
            onDone={async () => {
              const r = await me();
              setCustomer(r.customer);
              navigate('/dashboard');
            }}
          />
        )}

        {!loading && route === 'login' && !customer && (
          <Login onVerify={(c) => { setCustomer(c); navigate('/dashboard'); }} />
        )}

        {!loading && route === 'dashboard' && customer && (
          <Dashboard
            customer={customer}
            onConnectStripe={() => navigate('/connect/stripe')}
          />
        )}

        {!loading && route === 'connect-stripe' && customer && (
          <ConnectStripe customer={customer} onDone={() => navigate('/dashboard')} />
        )}

        {!loading && route === 'connect-stripe-return' && customer && (
          <ConnectStripe customer={customer} onDone={() => navigate('/dashboard')} justReturned />
        )}

        {!loading && route !== 'login' && route !== 'verify' && !customer && (
          <Login onVerify={(c) => { setCustomer(c); navigate('/dashboard'); }} />
        )}
      </main>
    </div>
  );
}

const shellStyle: CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
};
const headerStyle: CSSProperties = {
  padding: '16px 24px',
  borderBottom: `1px solid ${COLORS.sand}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};
const mainStyle: CSSProperties = {
  flex: 1,
  padding: '32px 24px',
  maxWidth: 900,
  margin: '0 auto',
  width: '100%',
};
const smallBtn: CSSProperties = {
  padding: '6px 12px',
  borderRadius: 999,
  background: 'transparent',
  border: `1px solid ${COLORS.sand}`,
  cursor: 'pointer',
  fontSize: 10,
  letterSpacing: '0.3em',
  textTransform: 'uppercase',
  fontWeight: 700,
  color: COLORS.ink,
};
